import seek, { seekTypes } from '@adc/seek'
import type {
  CollectionResult,
  CognitionEvaluationDataSet,
  CognitionEvaluationJudge,
  CognitionEvaluationMethod,
  CognitionEvaluationTaskName,
  CognitionPrompt,
  CognitionPromptInput,
  CognitionPromptsFilter,
} from '@adc/sdk/types'
import fetch from '@adc/fetch'
import { gql } from '@adc/sdk'

type FetchResult = {
  data: CollectionResult
  errors: Record<string, unknown>[]
}

type SeekAggregation = {
  alias?: string
  fields: string[]
}

type fetchAiEvaluationsArgs = {
  seekType: seekTypes
  operationName: string
  dsl: Parameters<seek['dsl']>['0'] // grab the dsl (param) type that seek.dsl() will expect
  fields: string[]
  fieldsConfig?: Parameters<seek['fields']>[1] // grab the fieldsConfig (param) type that seek.fields() will expect
  sortField: string
  sortDirection: string
  paginationState: {
    pageSize: number
    offset: number
  }
  controller?: AbortController
  aggregations?: SeekAggregation[]
}

export const fetchAiEvaluations = ({
  seekType,
  operationName,
  dsl,
  fields,
  fieldsConfig,
  sortField,
  sortDirection,
  paginationState,
  controller,
  aggregations,
}: fetchAiEvaluationsArgs): Promise<FetchResult> => {
  const seeker = seek
    .for(seekType)
    .operationName(operationName)
    .dsl(dsl)
    .fields(fields, fieldsConfig)
    .sortByField(sortField, sortDirection)
    .size(paginationState.pageSize)
    .offset(paginationState.offset)
  if (aggregations) seeker.implicitAggregations(aggregations)

  return seeker
    .execute(controller ? { controller } : undefined)
    .then(({ data, errors }) => {
      const result = {
        data: data.seek, // eslint-disable-line
        errors, // eslint-disable-line
      }
      return result
    })
}

export const assignEvaluationGroups = ({
  assigneeUrn,
  dsl,
}: {
  assigneeUrn: string
  dsl: Record<string, unknown>
}): Promise<boolean> =>
  fetch
    .forAuctionGraph<'assignEvaluationGroups', boolean>({
      variables: {
        assignee: assigneeUrn,
        dsl,
      },
      query: gql`
        mutation assignEvaluationGroups($assignee: URN!, $dsl: JSONObject!) {
          assignEvaluationGroups(assignee: $assignee, dsl: $dsl)
        }
      `,
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }

      return response.body.data.assignEvaluationGroups
    })

export const fetchDatasetsForTask = (
  task: string
): Promise<Array<CognitionEvaluationDataSet>> =>
  fetch
    .forAuctionGraph<'data_sets', Array<CognitionEvaluationDataSet>>({
      query: gql`
        query fetchDatasets($task: CognitionEvaluationTaskName!) {
          data_sets(task: $task) {
            description
            urn
            judges_urns
          }
        }
      `,
      variables: { task },
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }

      return response.body.data.data_sets
    })

export const fetchJudges = (
  task: CognitionEvaluationTaskName
): Promise<Array<CognitionEvaluationJudge>> =>
  fetch
    .forAuctionGraph<'judges', Array<CognitionEvaluationJudge>>({
      query: gql`
        query fetchJudges($filters: CognitionEvaluationJudgesFilter) {
          judges(filters: $filters) {
            description
            urn
            type
            task
          }
        }
      `,
      variables: {
        filters: {
          task,
        },
      },
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }

      return response.body.data.judges
    })

export const fetchJudge = (urn: string): Promise<CognitionEvaluationJudge> =>
  fetch
    .forAuctionGraph<'judge', CognitionEvaluationJudge>({
      query: gql`
        query fetchJudge($urn: URN!) {
          judge(urn: $urn) {
            description
            urn
            type
            judge_prompt
          }
        }
      `,
      variables: { urn },
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }

      return response.body.data.judge
    })

export const createJudge = ({
  description,
  judge_prompt,
  task,
}: {
  description: string
  judge_prompt: string
  task: CognitionEvaluationTaskName
}): Promise<CognitionEvaluationJudge> =>
  fetch
    .forAuctionGraph<'createJudge', CognitionEvaluationJudge>({
      query: gql`
        mutation createJudge($input: CognitionEvaluationLlmJudgeInput!) {
          createJudge(input: $input) {
            description
            urn
            type
            judge_prompt
          }
        }
      `,
      variables: {
        input: {
          description,
          judge_prompt,
          task,
        },
      },
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }

      return response.body.data.createJudge
    })

export const updateJudge = ({
  urn,
  description,
  judge_prompt,
  task,
}: {
  urn: string
  description: string
  judge_prompt: string
  task: CognitionEvaluationTaskName
}): Promise<CognitionEvaluationJudge> =>
  fetch
    .forAuctionGraph<'updateJudge', CognitionEvaluationJudge>({
      query: gql`
        mutation updateJudge(
          $urn: URN!
          $input: CognitionEvaluationLlmJudgeInput!
        ) {
          updateJudge(urn: $urn, input: $input) {
            description
            urn
            type
            judge_prompt
          }
        }
      `,
      variables: {
        urn,
        input: {
          description,
          judge_prompt,
          task,
        },
      },
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }

      return response.body.data.updateJudge
    })

export const fetchDataset = (
  urn: string
): Promise<CognitionEvaluationDataSet> =>
  fetch
    .forAuctionGraph<'data_set', CognitionEvaluationDataSet>({
      query: gql`
        query fetchDataset($urn: URN!) {
          data_set(urn: $urn) {
            judges_urns
            description
            cases {
              input {
                __typename
                type
                ... on CognitionEvaluationChatInputMessage {
                  object {
                    message
                  }
                }
              }
              output_description
            }
          }
        }
      `,
      variables: { urn },
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }

      return response.body.data.data_set
    })

export const createDataset = ({
  task,
  description,
  cases,
  judges_urns,
}: {
  task: string
  description: string
  cases: Array<{
    input: Record<string, unknown>
    output_description: string
  }>
  judges_urns: CognitionEvaluationJudge['urn'][]
}): Promise<{ urn: string }> =>
  fetch
    .forAuctionGraph<'createDataSet', { urn: string }>({
      query: gql`
        mutation createDataSet($input: CognitionEvaluationDataSetInput!) {
          createDataSet(input: $input) {
            urn
          }
        }
      `,
      variables: {
        input: {
          task,
          description,
          cases,
          judges_urns,
        },
      },
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }

      return response.body.data.createDataSet
    })

export const updateDataset = ({
  urn,
  task,
  description,
  cases,
  judges_urns,
}: {
  urn: string
  task: string
  description: string
  cases: Array<{
    input: Record<string, unknown>
    output_description: string
  }>
  judges_urns: CognitionEvaluationJudge['urn'][]
}): Promise<{ urn: string }> =>
  fetch
    .forAuctionGraph<'updateDataSet', { urn: string }>({
      query: gql`
        mutation updateDataSet(
          $urn: URN!
          $input: CognitionEvaluationDataSetInput!
        ) {
          updateDataSet(urn: $urn, input: $input) {
            urn
          }
        }
      `,
      variables: {
        urn,
        input: {
          task,
          description,
          cases,
          judges_urns,
        },
      },
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }

      return response.body.data.updateDataSet
    })

export const runDataset = ({
  task,
  dataSetURN,
  context,
}: {
  task: string
  dataSetURN: string
  context?: {
    run_as?: string
    sources: Array<string>
  }
}): Promise<boolean> =>
  fetch
    .forAuctionGraph<'runDataSet', boolean>({
      query: gql`
        mutation runDataSet(
          $task: CognitionEvaluationTaskName!
          $dataSetURN: URN!
          $context: CognitionEvaluationContextInput
        ) {
          runDataSet(task: $task, dataSetURN: $dataSetURN, context: $context)
        }
      `,
      variables: {
        task,
        dataSetURN,
        context,
      },
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }

      return response.body.data.runDataSet
    })

export const fetchPrompts = (
  filters: CognitionPromptsFilter
): Promise<Array<CognitionPrompt>> =>
  fetch
    .forAuctionGraph<'prompts', Array<CognitionPrompt>>({
      query: gql`
        query fetchPrompts($filters: CognitionPromptsFilter!) {
          prompts(filters: $filters) {
            urn
            evaluation_method_code
            text
            description
            status
            created_date
            updated_date
          }
        }
      `,
      variables: { filters },
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }

      return response.body.data.prompts
    })

export const fetchDefaultPrompt = (
  method: CognitionEvaluationMethod
): Promise<CognitionPrompt | null> =>
  fetch
    .forAuctionGraph<'default_prompt', CognitionPrompt | null>({
      query: gql`
        query fetchDefaultPrompt($method: CognitionEvaluationMethod!) {
          default_prompt(method: $method) {
            urn
            evaluation_method_code
            text
            description
            status
            created_date
            updated_date
          }
        }
      `,
      variables: { method },
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }

      return response.body.data.default_prompt
    })

export const upsertPrompt = (
  input: CognitionPromptInput
): Promise<CognitionPrompt> =>
  fetch
    .forAuctionGraph<'upsertPrompt', CognitionPrompt>({
      query: gql`
        mutation upsertPrompt($input: CognitionPromptInput!) {
          upsertPrompt(input: $input) {
            urn
            evaluation_method_code
            text
            description
            status
            created_date
            updated_date
          }
        }
      `,
      variables: { input },
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }

      return response.body.data.upsertPrompt
    })

export const deletePrompts = (urns: string[]): Promise<boolean> =>
  fetch
    .forAuctionGraph<'deletePrompts', boolean>({
      query: gql`
        mutation deletePrompts($urns: [URN!]!) {
          deletePrompts(urns: $urns)
        }
      `,
      variables: { urns },
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }

      return response.body.data.deletePrompts
    })

export const updateEvaluationGroup = ({
  urn,
  name,
  view_status,
}: {
  urn: string
  name?: string
  view_status: 'ACTIVE' | 'INACTIVE'
}): Promise<{ urn: string }> =>
  fetch
    .forAuctionGraph<'updateEvaluationGroup', { urn: string }>({
      query: gql`
        mutation updateEvaluationGroup(
          $urn: URN!
          $params: CognitionEvaluationGroupUpdateInput!
        ) {
          updateEvaluationGroup(urn: $urn, params: $params) {
            urn
          }
        }
      `,
      variables: {
        urn,
        params: {
          ...(name !== undefined && { name }),
          view_status,
        },
      },
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }

      return response.body.data.updateEvaluationGroup
    })

export interface CognitionTaskMetadata {
  task: string
  context_resources_supported: string[]
  task_interaction_type: string | null
}

export const fetchAllowedCognitionTasks = (): Promise<
  CognitionTaskMetadata[]
> =>
  fetch
    .forAuctionGraph<
      'current_user',
      { allowed_cognition_tasks: CognitionTaskMetadata[] }
    >({
      query: gql`
        query fetchAllowedCognitionTasks {
          current_user {
            allowed_cognition_tasks {
              task
              context_resources_supported
              task_interaction_type
            }
          }
        }
      `,
    })
    .then(response => {
      if (response.body.errors && response.body.errors[0]) {
        throw response.body.errors[0]
      }

      return response.body.data.current_user.allowed_cognition_tasks
    })
